<template>
  <div>
    <v-switch v-model="fieldEnabledModel" inset class="mt-0">
      <template v-slot:label>
        <TextBody :text="switchLabel" large />
      </template>
    </v-switch>
    <div class="ml-sm-14" @click="setFieldEnabled(true)">
      <div style="margin-bottom: 8px" v-if="fieldInfo" v-html="fieldInfo"></div>
      <SuusTextField
        :placeholder="fieldPlaceholder"
        :label="fieldLabel"
        v-model="valueModel"
        hide-details
        :disabled="!fieldEnabledRef"
        @blur="disableIfEmptyFunction"
        :button-text="buttonText"
        :disable-button="disableButton"
        :hide-button="hideButton"
        @buttonClick="$emit('buttonClick')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  ref,
  Ref,
  watch,
  WritableComputedRef,
} from "vue"
import TextBody from "components/UI/TextBody.vue"
import SuusTextField from "app/ui-primitives/forms/SuusTextField.vue"

export default defineComponent({
  name: "ToggleableTextField",
  components: { SuusTextField, TextBody },
  props: {
    fieldEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: String,
      required: false,
      default: "",
    },
    switchLabel: {
      type: String,
      required: false,
      default: "",
    },
    fieldPlaceholder: {
      type: String,
      required: false,
      default: "",
    },
    fieldLabel: {
      type: String,
      required: false,
      default: "",
    },
    fieldInfo: {
      type: String,
      required: false,
      default: null,
    },
    disableIfEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonText: {
      type: String,
      required: false,
      default: "",
    },
    disableButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const fieldEnabledRef: Ref<boolean> = ref(props.fieldEnabled)

    watch(
      () => props.fieldEnabled,
      (value) => {
        if (props.disableIfEmpty && !value) {
          return
        }
        fieldEnabledRef.value = value
      }
    )

    const fieldEnabledModel: WritableComputedRef<boolean> = computed({
      get: () => fieldEnabledRef.value,
      set: (value) => {
        setFieldEnabled(value)
      },
    })

    const setFieldEnabled = (value: boolean) => {
      fieldEnabledRef.value = !!value
      context.emit("update:fieldEnabled", !!value)
      if (!value) {
        context.emit("input", null)
      }
    }

    const valueModel: WritableComputedRef<string> = computed({
      get: () => props.value,
      set: (value) => {
        context.emit("input", value)
      },
    })

    const disableIfEmptyFunction = () => {
      if (props.disableIfEmpty && !valueModel.value) {
        fieldEnabledRef.value = false
      }
    }

    return {
      fieldEnabledModel,
      fieldEnabledRef,
      setFieldEnabled,
      valueModel,
      disableIfEmptyFunction,
    }
  },
})
</script>
